function safeDecodeURIComponent(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

/** Um arquivo persistido em campo FILE (JSON no valor do card). */
export interface FileFieldAttachment {
  id?: string;
  fileName?: string;
  originalFileName?: string;
  storageKey?: string;
  url?: string;
  assetUrl?: string;
  fileUrl?: string;
  path?: string;
}

function readString(r: Record<string, unknown>, keys: string[]): string {
  for (const k of keys) {
    const v = r[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

function normalizeAttachmentEntry(raw: unknown): FileFieldAttachment | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const id = readString(r, ['id', 'Id', 'attachmentId']);
  const fileName = readString(r, ['originalFileName', 'OriginalFileName', 'fileName', 'FileName']);
  const storageKey = readString(r, ['storageKey', 'StorageKey', 'key']);
  const url =
    readString(r, ['url', 'Url', 'downloadUrl', 'publicUrl']) ||
    readString(r, ['assetUrl', 'AssetUrl', 'fileUrl', 'FileUrl', 'path', 'Path', 'storagePath']);
  if (!id && !storageKey && !url && !fileName) return null;
  return {
    ...(id ? { id } : {}),
    ...(fileName ? { fileName } : {}),
    ...(storageKey ? { storageKey } : {}),
    ...(url ? { url } : {}),
  };
}

/**
 * Interpreta valor persistido: um objeto, array de objetos ou string JSON equivalente.
 */
export function parseFileFieldValueToAttachments(raw: unknown): FileFieldAttachment[] {
  if (raw == null) return [];
  let text: string;
  if (typeof raw === 'string') {
    text = raw.trim();
    if (!text) return [];
  } else if (typeof raw === 'object') {
    try {
      text = JSON.stringify(raw);
    } catch {
      return [];
    }
  } else {
    return [];
  }
  if (!text.startsWith('{') && !text.startsWith('[')) return [];
  try {
    const p = JSON.parse(text) as unknown;
    if (Array.isArray(p)) {
      return p.map(normalizeAttachmentEntry).filter((x): x is FileFieldAttachment => x != null);
    }
    const one = normalizeAttachmentEntry(p);
    return one ? [one] : [];
  } catch {
    return [];
  }
}

export function mergeFileFieldAttachmentsJson(
  existingJson: string | null | undefined,
  newPieces: FileFieldAttachment[]
): string {
  const cur = parseFileFieldValueToAttachments(existingJson ?? '');
  return JSON.stringify([...cur, ...newPieces]);
}

/** Nome amigável para exibir (sem duplicar extensão visível). */
export function fileAttachmentDisplayName(entry: FileFieldAttachment): string {
  const name = (entry.originalFileName || entry.fileName || '').trim();
  if (name) return name;
  const url =
    (entry.url || entry.assetUrl || entry.fileUrl || entry.path || '').trim() ||
    (entry.storageKey || '').trim();
  if (!url) return '';
  try {
    const path = new URL(url, 'https://placeholder.invalid').pathname;
    const seg = path.split('/').filter(Boolean).pop()?.trim() ?? '';
    if (seg) return safeDecodeURIComponent(seg);
  } catch {
    /* relative */
  }
  const fromPath = url.split('/').pop()?.trim() ?? '';
  return fromPath ? safeDecodeURIComponent(fromPath) : url;
}

/**
 * Evita "arquivo.pdf" + " .pdf" na UI quando o nome já traz a extensão.
 */
export function formatAttachmentExtensionSizeLine(
  displayName: string,
  extension: string,
  sizeLabel: string
): string {
  const ext = (extension || '').trim();
  const size = (sizeLabel || '').trim();
  if (!ext) return size;
  const dotExt = ext.startsWith('.') ? ext.toLowerCase() : `.${ext.toLowerCase()}`;
  const name = (displayName || '').trim().toLowerCase();
  if (name.endsWith(dotExt)) {
    return size;
  }
  const parts = [ext, size].filter(Boolean);
  return parts.join(' · ');
}

/**
 * Campos FILE costumam persistir como JSON: `{ fileName, storageKey, url? }` ou array para múltiplos.
 * Usado em listas, board e previews onde não se renderiza o input de upload.
 */
export function formatFileFieldValueForList(raw: unknown, attachedFallback: string): string {
  if (raw == null) return '';
  if (typeof raw !== 'string') {
    return String(raw).trim();
  }
  const trimmed = raw.trim();
  if (!trimmed) return '';
  if (
    (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
    (trimmed.startsWith('[') && trimmed.endsWith(']'))
  ) {
    const items = parseFileFieldValueToAttachments(trimmed);
    if (items.length === 0) return attachedFallback;
    if (items.length === 1) {
      const n = fileAttachmentDisplayName(items[0]!) || '';
      return n || attachedFallback;
    }
    const first = fileAttachmentDisplayName(items[0]!) || attachedFallback;
    return `${first} +${items.length - 1}`;
  }

  return trimmed;
}

export function looksLikeFileFieldJsonString(value: string): boolean {
  const t = value.trim();
  if (t.startsWith('[')) {
    return /"fileName"|"storageKey"|"fileUrl"|"url"|"id"/i.test(t);
  }
  if (!t.startsWith('{') || !t.endsWith('}')) return false;
  return t.includes('"fileName"') || t.includes('"storageKey"') || t.includes('"url"');
}
